/**
 * Reduce Labs Dialog
 * Dialog for instructors to terminate unassigned lab instances
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle, AlertTriangle, Plus, Minus } from 'lucide-react';

interface ReduceLabsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className: string;
  unassignedCount: number;
  onReduce: (count: number) => Promise<void>;
}

export function ReduceLabsDialog({
  open,
  onOpenChange,
  className,
  unassignedCount,
  onReduce,
}: ReduceLabsDialogProps) {
  const [count, setCount] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setCount(Math.min(1, unassignedCount));
      setError(null);
    }
  }, [open, unassignedCount]);

  const handleIncrement = () => {
    setCount((prev) => Math.min(prev + 1, unassignedCount, 50));
  };

  const handleDecrement = () => {
    setCount((prev) => Math.max(prev - 1, 1));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      setCount(Math.min(Math.max(value, 1), unassignedCount, 50));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (count < 1 || count > unassignedCount) {
      setError(`Count must be between 1 and ${unassignedCount}`);
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await onReduce(count);
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to reduce instances');
    } finally {
      setIsSubmitting(false);
    }
  };

  const noUnassigned = unassignedCount <= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reduce Lab Instances</DialogTitle>
          <DialogDescription>
            Terminate unassigned lab instances for <span className="font-medium">{className}</span>.
            This helps reduce costs when fewer students than expected enroll.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {noUnassigned ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No unassigned instances available to terminate.
                All provisioned instances are assigned to students.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Current status */}
              <div className="rounded-lg bg-muted p-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Unassigned instances:</span>
                  <span className="font-medium">{unassignedCount}</span>
                </div>
              </div>

              {/* Warning */}
              <Alert variant="default" className="border-amber-200 bg-amber-50 text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Oldest unassigned instances will be terminated first (FIFO).
                  This action cannot be undone.
                </AlertDescription>
              </Alert>

              {/* Count input */}
              <div className="space-y-2">
                <Label htmlFor="count">Number of Instances to Terminate</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleDecrement}
                    disabled={count <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    id="count"
                    type="number"
                    min={1}
                    max={Math.min(unassignedCount, 50)}
                    value={count}
                    onChange={handleInputChange}
                    className="w-24 text-center"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleIncrement}
                    disabled={count >= unassignedCount || count >= 50}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Termination info */}
              <p className="text-xs text-muted-foreground">
                Termination takes 60-90 seconds per instance. CloudFormation stacks will be deleted.
              </p>
            </>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={isSubmitting || noUnassigned}
            >
              {isSubmitting ? 'Terminating...' : `Terminate ${count} Instance${count !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
